<template>
	<section class="section-base form-section-base">
		<div class="form-section-base__block-outer">
			<div class="form-section-base__block-inner">
				<div class="form-section-base__title">Сведения о предыдущих кредиторах</div>
				<div class="form-section-base__sub-title">
					(заполняется, если кредитор, указанный в документе, на основании которого
					совершается исполнительная надпись, отличается от кредитора, направляющего
					настоящее заявление)
				</div>

				<div class="form-section-base__controls">
					<v-row>
						<v-col>
							<v-checkbox
								v-model="isCreditors"
								:disabled="isSurcharge"
								label="Кредитор, указанный в документе, на основании которого совершается исполнительная надпись, отличается от кредитора, направляющего настоящее заявление"
								hide-details
							/>
						</v-col>
					</v-row>
				</div>

				<template v-if="isCreditors">
					<Creditor
						v-for="(v, index) in $v.creditors.$each.$iter"
						:v="v"
						:key="index"
						:can-delete="creditors.length > 1 && !isSurcharge"
						:disabled="isSurcharge"
						@delete="deleteCreditor(index)"
						@change-reason="changeReasonHandler({ index, value: $event })"
					/>
					<v-row>
						<v-col v-if="!isSurcharge" cols="12">
							<v-btn
								depressed
								color="primary"
								class="button-base"
								@click="addCreditor"
							>
								<v-icon size="20" left>{{ mdiAccountMultiplePlus }}</v-icon>
								Добавить кредитора
							</v-btn>
						</v-col>
					</v-row>
				</template>
			</div>
		</div>
		<div class="form-section-base__block-outer" v-if="isCreditors">
			<div class="form-section-base__block-inner">
				<v-col cols="12" class="mt-8">
					<div class="form-base__title">
						Документы, подтверждающие изменение кредитора
					</div>
				</v-col>
				<v-col cols="12">
					<AttachDocumentMultiple
						v-model="$v.documents.$model"
						:errorMessages="getValidationErrorText($v.documents)"
						:disabled="isSurcharge"
            :withSelect="statementType === StatementType.NOTICE_RECOVERY && isNoticeRecoveryClassifierEnable"
						:document-types='documentTypes'
						translationKey="noticeRecovery.creditor.attachMultiple"
					/>
				</v-col>
			</div>
		</div>
		<v-container>
			<Footer
				@next-btn-click="btnSaveClickHandler(StepDirection.NEXT)"
				@prev-btn-click="btnSaveClickHandler(StepDirection.PREV)"
				:loading-next="loadingNext"
			/>
		</v-container>
	</section>
</template>

<script>
import {requiredIf} from 'vuelidate/lib/validators';
import {mdiAccountMultiplePlus} from '@mdi/js';
import cloneDeep from 'clone-deep';

import {checkInvalid, leaveStep} from '@/utils/services';
import {DOCS_REQUIREMENTS, getValidationErrorText} from '@/utils/validate';
import {Role} from '@/utils/dictionaries';
import {ServicesModule} from '@/store/modules/services';
import AttachDocumentMultiple from '@/components/base/AttachDocument/AttachDocumentMultiple';
import Footer from '@/components/services/elements/Footer';
import {StepDirection, StepName} from '@/types/services';
import Creditor, {creditorModel, getCreditorValidation} from './Creditor.vue';
import {NoticeRecoveryStep, StatementType} from '@/constants';
import {UploadDocumentTypes} from "@/utils/uploadDocumentTypes";
import { isNoticeRecoveryClassifierEnable } from '@/app.config';

export default {
	name: 'Creditors',
	props: {
		loadingNext: {
			type: Boolean,
			required: true,
		},
		isSurcharge: {
			type: Boolean,
			required: true,
		},
		statementType: {
			type: String,
			required: true,
		},
	},
	components: {Creditor, AttachDocumentMultiple, Footer},
	data: () => ({
		leaveStep,
		StepName,
		StepDirection,
		Role,
		getValidationErrorText,
		mdiAccountMultiplePlus,
		isCreditors: false,
		invalidFields: [],
		creditors: [cloneDeep(creditorModel)],
		documents: [],
		changeReasonValues: new Set(),
		documentsRequired: false,
		applicant: {},
		unsubscribe: null,
		StatementType,
    isNoticeRecoveryClassifierEnable,
	}),
	validations() {
		return {
			creditors: {
				$each: getCreditorValidation(this.applicant),
			},
			documents: {
				DOCUMENTS_MIN_LENGTH: requiredIf(() => this.documentsRequired),
				DOCS_REQUIREMENTS: DOCS_REQUIREMENTS(this.documents)
			},
		};
	},
	watch: {
		documentsRequired(val, oldVal) {
			if (val !== oldVal) this.$v.documents.$reset();
		},
	},
	created() {
		this.prefillDraft();
		this.fillApplicant();

		this.unsubscribe = this.$store.subscribeAction((action) => {
			if (action.type === 'saveStatement') this.fillApplicant();
		});
	},
	beforeDestroy() {
		this.unsubscribe();
	},
	computed: {
		documentTypes() {
			const docsCodes = ['12104']
			return UploadDocumentTypes
				.filter((item) => ['12100'].includes(item.groupCode))
				.map((item) => {
					return {
						...item,
						documents: item.documents.filter((doc => docsCodes.includes(doc.value)))
					}
				})
		}
	},
	methods: {
		fillApplicant() {
			this.applicant = cloneDeep(ServicesModule.statement.subjects.applicant);
		},

		changeReasonHandler({index, value}) {
			if (value) {
				this.changeReasonValues.add(index);
			} else {
				this.changeReasonValues.delete(index);
			}

			this.documentsRequired = Boolean(this.changeReasonValues.size);
		},

		touchFields() {
			this.invalidFields = [];

			if (!this.isCreditors) return;

			this.$v.documents.$touch();
			checkInvalid(this.$v.documents, this.invalidFields);

			for (const creditor of Object.values(this.$v.creditors.$each.$iter)) {
				creditor[creditor.role.$model].$touch();
				checkInvalid(creditor[creditor.role.$model], this.invalidFields);
			}
		},

		assemblyModel() {
			const model = {
				previousCreditors: [],
				prevCreditorDocuments: [],
			};

			if (!this.isCreditors) return model;

			const previousCreditors = this.creditors.map((item) => {
				if (item.role === Role.PERSON) {
					const itemModel = cloneDeep(item[Role.PERSON]);

					// если не является ИП, удаляем лишнии поля
					const businessmanArr = ['ogrn', 'emailUl'];
					if (!item[Role.PERSON].isBusinessman) {
						businessmanArr.forEach((key) => {
							delete itemModel[key];
						});
					}

					return {
						[Role.PERSON]: {
							...itemModel,
						},
						changeReason: item[Role.PERSON].changeReason,
					};
				}

				if (item.role === Role.ORGANIZATION) {
					const itemModel = cloneDeep(item[Role.ORGANIZATION]);
					return {
						[Role.ORGANIZATION]: {
							...itemModel,
						},
						changeReason: item[Role.ORGANIZATION].changeReason,
					};
				}

				if (item.role === Role.FOREIGN_ORGANIZATION) {
					const itemModel = cloneDeep(item[Role.FOREIGN_ORGANIZATION]);
					return {
						[Role.FOREIGN_ORGANIZATION]: {
							...itemModel,
						},
						changeReason: item[Role.FOREIGN_ORGANIZATION].changeReason,
					};
				}
			});
			const prevCreditorDocuments = cloneDeep(this.documents);

			return {
				previousCreditors,
				prevCreditorDocuments,
			};
		},

		prefillDraft() {
			if (
				!ServicesModule.statement.subjects.previousCreditors ||
				ServicesModule.statement.subjects.previousCreditors.length === 0
			) {
				return;
			}

			this.isCreditors = true;

			const previousCreditorsStore = cloneDeep(
				ServicesModule.statement.subjects.previousCreditors
			);

			this.creditors = previousCreditorsStore.map((item, itemIndex) => {
				let itemRole = null;
				if (item[Role.PERSON]) itemRole = Role.PERSON;
				if (item[Role.ORGANIZATION]) itemRole = Role.ORGANIZATION;
				if (item[Role.FOREIGN_ORGANIZATION])
					itemRole = Role.FOREIGN_ORGANIZATION;

				let itemDefaults = [];
				if (itemRole === Role.PERSON)
					itemDefaults = [Role.ORGANIZATION, Role.FOREIGN_ORGANIZATION];
				if (itemRole === Role.ORGANIZATION)
					itemDefaults = [Role.PERSON, Role.FOREIGN_ORGANIZATION];
				if (itemRole === Role.FOREIGN_ORGANIZATION)
					itemDefaults = [Role.PERSON, Role.ORGANIZATION];

				this.changeReasonHandler({
					index: itemIndex,
					value: item.changeReason,
				});

				return {
					role: itemRole,
					[itemRole]: {
						...cloneDeep(creditorModel[itemRole]),
						...cloneDeep(item[itemRole]),
						changeReason: item.changeReason,
					},
					[itemDefaults[0]]: cloneDeep(creditorModel[itemDefaults[0]]),
					[itemDefaults[1]]: cloneDeep(creditorModel[itemDefaults[1]]),
				};
			});

			if (ServicesModule.statement.prevCreditorDocuments.length === 0) {
				return;
			}

			this.documents = cloneDeep(
				ServicesModule.statement.prevCreditorDocuments
			);
		},

		addCreditor() {
			this.creditors.push(cloneDeep(creditorModel));
		},

		deleteCreditor(deleteIndex) {
			this.creditors = this.creditors.filter(
				(_, index) => index !== Number(deleteIndex)
			);
		},

		btnSaveClickHandler(direction) {
			leaveStep({
				stepThis: this,
				direction,
				step: NoticeRecoveryStep.CREDITORS,
			});
		},
	},
};
</script>
